import { useMemo } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '@clerk/clerk-react';
import { ArrowLeft, Compass, Printer } from 'lucide-react';
import { usePartner, useUser } from '../lib/api/useUser';
import { resolvePersona } from '../lib/personas';
import { readChatHistory } from '../lib/storage';
/**
 * Memory Page — a free, persona-voiced keepsake of the user's stay.
 *
 * v1 SCOPE
 *   - Source of truth for the user record (townSlug + profile) is the
 *     API via `useUser()`. Requires a Clerk session — without one, the
 *     query is disabled and the page bounces home. That's correct
 *     behaviour for v1 (single-device, signed-in-owner only); real
 *     public sharing lands with API Phase 8 which will introduce a
 *     dedicated `/v1/memory-pages/:publicToken` endpoint that resolves
 *     a snapshot without requiring auth.
 *   - The URL token must match the user's town slug. In v1 the `:token`
 *     URL parameter IS the town slug (since 25b unified them); when
 *     public sharing lands, the route shape stays the same but the
 *     token becomes a random URL-safe string mapped server-side.
 *   - Composition uses both the user's profile (from the API) and any
 *     persisted chat history (still localStorage in v1 — see Phase 3
 *     for server-side chat history). Profile drives the existing
 *     3-paragraph narrative; history drives the day-by-day timeline +
 *     "where you went" enrichment sections.
 *
 * PARTNER ATTRIBUTION (Batch 8a)
 *   The effective persona is resolved via `resolvePersona(townSlug,
 *   partner)`, so partner-attributed users see their partner persona
 *   (e.g. Ollie at Harlyn Sands) drive the keepsake — title, subtitle,
 *   body opener, day summaries all flow from the partner persona's
 *   `composeMemoryPage`. Non-attributed users get the town persona
 *   (Morwenna, etc.) unchanged. Same branching pattern as MePage and
 *   JoinPage; mirrors API Batch 7a's buildSystemPrompt fallback.
 *
 *   Footer also branches on partner attribution: a "Hosted by
 *   {partner.name}" line is prepended above the product credit when a
 *   partner is attached, scaling cleanly across towns and partners
 *   without per-partner code. The line uses `partner` directly (not
 *   `partner.persona`) so the safety-net path still shows accurate
 *   attribution if the persona content failed Zod but the partner row
 *   is present.
 *
 *   `usePartner` and `useUser` share the same TanStack query cache
 *   key (`['users','me', userId]`) via the select pattern in
 *   `lib/api/useUser`, so reading both here costs one fetch.
 *
 * URL: /memories/:token (e.g. /memories/padstow)
 *
 * The visual style intentionally departs from the chat surface — this
 * is a "deliverable" the user might screenshot or share. Wider column,
 * generous vertical rhythm, prose-local serif throughout, compass-mark
 * dividers as a quiet TownInsider motif.
 *
 * Navigation: a single back arrow at the top-left of the article hugs
 * the same max-w-2xl column the rest of the content uses, so the
 * affordance reads as part of the page rather than floating UI. Target
 * is /me (chat surface), not the browser-back stack — users can land
 * here from Settings or from a deep-link in future, and /me is the
 * right home base in either case.
 *
 * SAVE-AS-PDF AFFORDANCE
 *   A Printer-icon button sits opposite the back arrow and calls
 *   `window.print()`. Browsers' print dialogs all offer "Save as PDF"
 *   on every desktop platform and iOS Safari's share-sheet print flow
 *   produces a PDF too. No Puppeteer / jsPDF dependency for v1 —
 *   server-side PDF generation lands with API Phase 6 (Stripe + the
 *   £4.95 deluxe-PDF SKU) and gives a higher-fidelity output for
 *   paying users. The browser-print path stays around as a free
 *   always-available fallback.
 *
 *   The full nav row (back arrow + print button) is hidden in print
 *   via `print:hidden`. The `print:bg-white` override on `<main>`
 *   prevents the cream tint from washing the printed output —
 *   keepsake content stays warm on screen and clean on paper / PDF.
 */
export default function MemoryPage() {
  const { token } = useParams<{ token: string }>();
  // Clerk loading state — `isLoaded` flips true once the SDK has
  // determined whether there's a session. Until then `isSignedIn` is
  // undefined and useUser's `enabled: !!isSignedIn` short-circuits.
  //
  // `userId` (10 Jun 2026 structural fix) — Clerk user id, used to
  // scope localStorage chat history reads per-user via the matching
  // signature change in src/lib/storage.ts. Without this, a guest
  // who signed in after another account on the same device could see
  // the previous account's chat reflected in their keepsake (e.g.
  // "Where you went" listing the prior user's pick-acks).
  const { isLoaded, isSignedIn, userId } = useAuth();
  const { data: user, isLoading, isError } = useUser();
  // Partner shares the same ['users','me', userId] cache via the
  // select pattern in lib/api/useUser — no extra network round-trip.
  // `partner` is PublicPartner | null | undefined; we coalesce
  // undefined to null because `resolvePersona` expects the
  // partner-or-null shape that /me returns server-side (Batch 7b-1).
  const { data: partner } = usePartner();
  const persona = useMemo(
    () => (user ? resolvePersona(user.townSlug, partner ?? null) : null),
    [user, partner],
  );
  // Read history alongside the user record. Returns null when no
  // history is persisted yet (cold-start visit on day 1 / fresh-onboard
  // user) OR when userId is briefly null while Clerk resolves;
  // composeMemoryPage handles the undefined case and returns the base
  // profile-driven content with empty enrichment arrays. `userId` in
  // the deps ensures the keepsake re-renders against the correct
  // per-user chat history once Clerk finishes loading.
  const history = useMemo(() => readChatHistory(userId), [userId]);
  // Briefly render nothing while Clerk or useUser is still resolving.
  // Almost never visible — RequireAuth-adjacent flows pre-warm the
  // cache, and Clerk's isLoaded resolves within one tick on most loads.
  if (!isLoaded || isLoading) return null;
  // Bounce conditions, all redirect home:
  //   - Not signed in (no auth → no profile → no memory page)
  //   - useUser query errored
  //   - User record missing or town slug unrecognised
  //   - User has no profile yet (mid-onboarding)
  //   - URL token doesn't match the user's town slug (cross-user URL
  //     on a device authenticated as someone else)
  if (
    !isSignedIn ||
    isError ||
    !user ||
    !persona ||
    !user.profile ||
    user.townSlug !== token
  ) {
    return <Navigate to="/" replace />;
  }
  // exactOptionalPropertyTypes — pass undefined explicitly only when
  // history is null. composeMemoryPage's second arg is `?` so undefined
  // is the "no history" signal.
  const memory = persona.composeMemoryPage(
    user.profile,
    history ?? undefined,
  );
  return (
    <>
      <Helmet>
        <title>{`${memory.title} \u00b7 ${persona.townLabel} Insider`}</title>
        {/* Page is private-by-default until API ships sharing — keep
            it out of search engines. */}
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>
      <main
        className="min-h-dvh bg-cream-50 print:bg-white"
        style={{
          paddingTop: 'env(safe-area-inset-top)',
          paddingBottom: 'env(safe-area-inset-bottom)',
        }}
      >
        <article className="mx-auto max-w-2xl px-6 py-12 sm:py-16">
          {/*
            Top nav row — back-to-chat arrow on the left, save-as-PDF
            print button on the right. Same circular hit-target +
            hover/focus treatment for both so they balance visually.
            The whole row is hidden in print (`print:hidden`) so the
            PDF/printed page starts cleanly with the compass mark.
          */}
          <div className="mb-8 flex items-center justify-between print:hidden">
            <Link
              to="/me"
              aria-label="Back to chat"
              className="inline-flex h-9 w-9 items-center justify-center rounded-full text-moor-700 transition-colors hover:bg-moor-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-moor-500/40 focus-visible:ring-offset-2"
            >
              <ArrowLeft className="h-5 w-5" aria-hidden="true" />
            </Link>
            <button
              type="button"
              onClick={() => window.print()}
              aria-label="Save as PDF"
              className="inline-flex h-9 w-9 items-center justify-center rounded-full text-moor-700 transition-colors hover:bg-moor-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-moor-500/40 focus-visible:ring-offset-2"
            >
              <Printer className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>
          {/* Compass mark — same motif as MePage header, ties this back
              to the TownInsider product. */}
          <div className="flex items-center justify-center">
            <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-moor-700 text-cream-50">
              <Compass className="h-7 w-7" aria-hidden="true" />
            </div>
          </div>
          <h1 className="prose-local mt-8 text-center text-4xl font-bold text-moor-900 sm:text-5xl">
            {memory.title}
          </h1>
          <p className="mt-3 text-center text-xs uppercase tracking-wide text-moor-600">
            {memory.subtitle}
          </p>
          <CompassDivider />
          <div className="prose-local space-y-6">
            {memory.paragraphs.map((paragraph, i) => (
              <p
                // Stable index keys are fine here — paragraphs are
                // generated in fixed order from a deterministic compose
                // fn; no reordering or filtering happens client-side.
                // eslint-disable-next-line react/no-array-index-key
                key={i}
                className="text-lg leading-relaxed text-moor-900"
              >
                {paragraph}
              </p>
            ))}
          </div>
          {/*
            Enrichment sections — timeline + places list. Each gates on
            its own non-empty array so a cold-start user (no history)
            sees the v1 page exactly as before, and a user mid-week
            sees only the sections their data supports (e.g. someone
            who's chatted but not picked anything yet sees the timeline
            but not the places list). Spacing/typography mirrors the
            page's existing rhythm rather than introducing new motifs.
          */}
          {memory.daysVisited.length > 0 && (
            <section className="mt-16">
              <h2 className="text-center text-xs font-semibold uppercase tracking-wide text-moor-500">
                Your week, day by day
              </h2>
              <ul className="mt-8 space-y-6">
                {memory.daysVisited.map((day) => (
                  <li key={day.dateLocal} className="text-center">
                    <p className="prose-local text-lg font-semibold text-moor-900">
                      {day.label}
                    </p>
                    <p className="prose-local mt-1 text-base leading-relaxed text-moor-700">
                      {day.summary}
                    </p>
                  </li>
                ))}
              </ul>
            </section>
          )}
          {memory.placesAcknowledged.length > 0 && (
            <section className="mt-12">
              <h2 className="text-center text-xs font-semibold uppercase tracking-wide text-moor-500">
                Where you went
              </h2>
              <ul className="mt-8 space-y-3">
                {memory.placesAcknowledged.map((place) => (
                  <li key={place.name} className="text-center">
                    <span className="prose-local text-lg text-moor-900">
                      {place.name}
                    </span>
                    <span
                      className="mx-2 text-moor-300"
                      aria-hidden="true"
                    >
                      &middot;
                    </span>
                    <span className="text-xs uppercase tracking-wide text-moor-500">
                      {place.dateLabel}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          )}
          {/*
            Kernewek phrase block (Batch 8a): gated on presence of
            `memory.cornishPhrase`. Town personas (Morwenna / Jago /
            Demelza) supply one and the block renders. Partner personas
            omit it — `cornishPhrase` is optional on MemoryPageContent
            so a Harlyn Sands keepsake doesn't carry a Cornish flourish
            that isn't the partner's. The preceding CompassDivider is
            gated together with the phrase so absence collapses cleanly
            into the closingLine below.
          */}
          {memory.cornishPhrase && (
            <>
              <CompassDivider />
              <div className="text-center">
                <p className="prose-local text-3xl font-bold text-moor-900">
                  {memory.cornishPhrase.word}
                </p>
                <p className="mt-2 text-sm italic text-moor-600">
                  {memory.cornishPhrase.meaning}
                </p>
              </div>
            </>
          )}
          <p className="prose-local mt-12 text-center text-lg italic text-moor-700">
            {memory.closingLine}
          </p>
          {/*
            Footer credits (Batch 8a, refined 11). Partner-attributed
            users get a "Hosted by {partner.name}" line above; gated on
            `partner` (not `partner.persona`) so the safety-net path
            (partner present but persona jsonb failed Zod) still
            surfaces accurate attribution.

            Product credit collapsed to one line: "Made with TownInsider
            by Autaimate" with "Autaimate" as a clickable external link
            to autaimate.com — the company that builds the product.
            Single credit pattern across both partner and town-only
            footers; no per-town flavour ("Made with the Padstow
            Insider") since the per-town narrative lives in the
            keepsake body, not the credit line.

            target="_blank" + rel="noopener noreferrer" per standard
            external-link hygiene — the memory page is a long-lived
            keepsake; users shouldn't lose it to navigation.
          */}
          <footer className="mt-16 space-y-1 text-center text-xs uppercase tracking-wide text-moor-500">
            {partner && <p>Hosted by {partner.name}</p>}
            <p>
              Made with TownInsider by{' '}
              <a
                href="https://autaimate.com"
                target="_blank"
                rel="noopener noreferrer"
                className="underline decoration-dotted underline-offset-2 transition-colors hover:text-moor-700"
              >
                Autaimate
              </a>
            </p>
          </footer>
        </article>
      </main>
    </>
  );
}
/**
 * Decorative section divider — thin rule on either side of a small
 * compass mark. Used between the title block, the narrative, and the
 * Cornish phrase to give the page a deliberate, document-like rhythm
 * without resorting to heavy `<hr>` rules.
 */
function CompassDivider() {
  return (
    <div className="my-12 flex items-center gap-3" aria-hidden="true">
      <div className="h-px flex-1 bg-moor-200" />
      <Compass className="h-4 w-4 text-moor-400" />
      <div className="h-px flex-1 bg-moor-200" />
    </div>
  );
}
